"use client";

import { useEffect, useState } from "react";
import { MIN_CONCURRENT_MAX_TEXTURE_SIZE } from "@/lib/webgl-capability";
import { DevDebugWidget } from "@/components/dev-debug-widget";

type DeviceMemoryNavigator = Navigator & { deviceMemory?: number };

type CapabilitySnapshot = {
  hardwareConcurrency: number | string;
  deviceMemory: number | string;
  pointerCoarse: boolean;
  hoverNone: boolean;
  viewportWidth: number;
  isMobileBreakpoint: boolean;
};

function readSnapshot(): CapabilitySnapshot {
  const nav = navigator as DeviceMemoryNavigator;
  return {
    hardwareConcurrency: nav.hardwareConcurrency ?? "unavailable",
    // Chrome/Chromium only -- always "unavailable" on Safari/Firefox, and deliberately coarse/capped
    // even where it exists (privacy fingerprinting mitigation), so this is here to visibly PROVE that
    // gap rather than to lean on the value for anything.
    deviceMemory: nav.deviceMemory ?? "unavailable (not Chrome)",
    pointerCoarse: window.matchMedia("(pointer: coarse)").matches,
    hoverNone: window.matchMedia("(hover: none)").matches,
    viewportWidth: window.innerWidth,
    // The CURRENT (viewport-width-only) signal driving isMobile everywhere else in this file's
    // ecosystem today -- shown alongside the others so it's obvious where it agrees/disagrees with
    // pointerCoarse on a given device (e.g. a wide-viewport tablet: isMobileBreakpoint false,
    // pointerCoarse true -- exactly the mismatch this whole debug view exists to make visible).
    isMobileBreakpoint: window.matchMedia("(max-width: 860px)").matches,
  };
}

type DecisionResult = { capable: boolean; decisionMs: number; detail: string; maxTextureSize?: number };

// --- Three competing "is this device capable of more concurrent WebGL contexts" approaches, run and
// timed side by side purely for THIS comparison view. #2 (webgl probe) is now the one actually gating
// /customize's real behavior (see lib/webgl-capability.ts) -- chosen over #3 (real FPS) purely for
// speed, once #3 had already proven #1 (static hardware specs) wrong on a real device. All three still
// run here so the full tradeoff stays visible on whatever device this loads on, not just reasoned about
// once and forgotten.

// Static hardware-spec check -- the approach tried FIRST and directly falsified by a real device (an
// 8-core budget Android tablet that measured far worse, via real FPS, than a 4-core iPhone). Kept here
// specifically so that mismatch stays visible on every device this runs on, not just the one it failed.
function checkHardwareSpec(): DecisionResult {
  const start = performance.now();
  const nav = navigator as DeviceMemoryNavigator;
  const cores = nav.hardwareConcurrency ?? 0;
  const memory = nav.deviceMemory; // undefined on Safari/Firefox -- treated as "no signal", not a fail
  const capable = cores >= 4 && (memory === undefined || memory >= 4);
  return { capable, decisionMs: performance.now() - start, detail: `cores=${cores}, memory=${memory ?? "n/a"}` };
}

// WebGL parameter/renderer-string probe -- a throwaway canvas+context, read and discarded immediately
// (no rendering, nothing persists). The capable verdict here reuses the EXACT same threshold
// (MIN_CONCURRENT_MAX_TEXTURE_SIZE) the real gating logic uses, not a separate debug-only number, so
// this panel shows the actual live decision rather than a shadow copy that could quietly drift from it.
// WEBGL_debug_renderer_info (the renderer string) is shown for extra context only -- it's increasingly
// masked by browsers for anti-fingerprinting and isn't part of the real decision.
function checkWebglCapability(): DecisionResult {
  const start = performance.now();
  const canvas = document.createElement("canvas");
  const gl = (canvas.getContext("webgl") ?? canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
  if (!gl) return { capable: false, decisionMs: performance.now() - start, detail: "no WebGL context" };
  const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
  const renderer = debugInfo ? String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)) : "masked/unavailable";
  const capable = maxTextureSize >= MIN_CONCURRENT_MAX_TEXTURE_SIZE;
  return {
    capable, decisionMs: performance.now() - start, maxTextureSize,
    detail: `maxTexture=${maxTextureSize}, renderer=${renderer}`,
  };
}

// Explicitly non-production debug aid -- exists to let capability signals (hardwareConcurrency,
// pointer/hover media features, the current width-based isMobile check, and all 3 candidate decision
// functions) be compared side by side on real devices while deciding how to gate WebGL concurrency on
// /customize, rather than guessing from spec sheets. Renders its content inside DevDebugWidget's generic
// collapsible shell (starts as a small round toggle, click to expand) -- explicit request so this can sit
// quietly out of the way rather than a panel permanently open over the page, and so future debug data
// (server-side, other UI state) has an obvious place to grow into without rebuilding the toggle mechanism.
// NODE_ENV is inlined at build time (not a runtime-varying value), so this return is safe before the
// hooks below -- it's equivalent to this whole component not existing at all in a production bundle, not
// a real conditional-hooks violation.
export function DeviceCapabilityDebug({
  measuredFps,
}: {
  // The primary tile's real FpsProbe reading (customize-preview-gallery.tsx), once available --
  // comparison data only now (see checkWebglCapability above for what's actually gating /customize).
  // decisionMs here is the ACTUAL elapsed sampling window for this run, not a hardcoded constant, so it
  // reflects real timing even if that window's length is ever tuned later.
  measuredFps?: { fps: number; capable: boolean; decisionMs: number } | null;
} = {}) {
  if (process.env.NODE_ENV === "production") return null;
  // eslint-disable-next-line react-hooks/rules-of-hooks -- see the NODE_ENV comment above
  const [snapshot, setSnapshot] = useState<CapabilitySnapshot | null>(null);
  const [hardwareSpec, setHardwareSpec] = useState<DecisionResult | null>(null);
  const [webglCapability, setWebglCapability] = useState<DecisionResult | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- first real read needs the actual browser APIs, not SSR
    setSnapshot(readSnapshot());
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    setHardwareSpec(checkHardwareSpec());
    // eslint-disable-next-line react-hooks/set-state-in-effect -- see above
    setWebglCapability(checkWebglCapability());
    const widthQuery = window.matchMedia("(max-width: 860px)");
    const update = () => setSnapshot(readSnapshot());
    widthQuery.addEventListener("change", update);
    window.addEventListener("resize", update);
    return () => {
      widthQuery.removeEventListener("change", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  if (!snapshot) return null;

  return (
    <DevDebugWidget label="device capability">
      <div>hardwareConcurrency: {snapshot.hardwareConcurrency}</div>
      <div>deviceMemory: {snapshot.deviceMemory}</div>
      <div>pointer: coarse: {String(snapshot.pointerCoarse)}</div>
      <div>hover: none: {String(snapshot.hoverNone)}</div>
      <div>viewport width: {snapshot.viewportWidth}px</div>
      <div>isMobile (≤860px): {String(snapshot.isMobileBreakpoint)}</div>
      {/* Its own short, dedicated, bold line -- explicit request after this being hard to spot buried
          inside the combined "webgl probe" line below, especially on a device whose renderer string is
          long enough to push it around or wrap awkwardly in this panel's narrow fixed width. */}
      <div className="device-capability-debug-highlight">
        maxTextureSize: {webglCapability?.maxTextureSize ?? "…"} (threshold: {MIN_CONCURRENT_MAX_TEXTURE_SIZE})
      </div>
      <div className="device-capability-debug-title">decision comparison</div>
      <div>
        1. spec check: {hardwareSpec ? `capable=${hardwareSpec.capable} (${hardwareSpec.decisionMs.toFixed(2)}ms) -- ${hardwareSpec.detail}` : "…"}
      </div>
      <div>
        2. webgl probe (used live): {webglCapability ? `capable=${webglCapability.capable} (${webglCapability.decisionMs.toFixed(2)}ms) -- ${webglCapability.detail}` : "…"}
      </div>
      <div>
        {/* Optional chaining on decisionMs specifically -- belt-and-suspenders against a stale build of
            FpsProbe's own module (an older shape without decisionMs) surviving a hot-reload while this
            file's already updated; confirmed harmless/transient once, but free to guard against
            regardless since a crashed debug overlay would otherwise take the rest of the page down with it. */}
        3. real FPS (comparison only): {measuredFps
          ? `${measuredFps.fps.toFixed(1)}fps, capable=${measuredFps.capable} (${measuredFps.decisionMs?.toFixed(0) ?? "?"}ms)`
          : "measuring…"}
      </div>
    </DevDebugWidget>
  );
}
